/**
 * Implementation of PHP's `array_chunk`
 * @see {@link https://www.php.net/manual/en/function.array-chunk.php}
 * @template Value
 * @param {Value[]} array
 * @param {number} size
 * @returns {Value[][]}
 */
function chunk(array, size) {
	const batched = [];

	for (let index = 0; index < array.length; index += size) {
		batched.push(array.slice(index, index + size));
	}

	return batched;
}

/**
 * @template {any} ArrayATypes
 * @template {any} ArrayBTypes
 * @param {ArrayATypes[]} a
 * @param {ArrayBTypes[]} b
 * @returns {ArrayATypes[]}
 */
function diff(a, b) {
	return a.filter(
		/**
		 * @param {any} value
		 * @returns {boolean}
		 */
		(value) => !b.includes(value)
	);
}

/**
 * @param {any[]} haystack
 * @param {...any} needleList
 * @returns {boolean}
 */
function excludesAll(haystack, ...needleList) {
	return needleList.every(
		/**
		 * @param {any} needle
		 * @returns {boolean}
		 */
		(needle) => !haystack.includes(needle)
	);
}

/**
 * @param {any[]} haystack
 * @param {...any} needleList
 * @returns {boolean}
 */
function excludesAny(haystack, ...needleList) {
	return needleList.some(
		/**
		 * @param {any} needle
		 * @returns {boolean}
		 */
		(needle) => !haystack.includes(needle)
	);
}

/**
 * @param {any[]} haystack
 * @param {...any} needleList
 * @returns {boolean}
 */
function includesAll(haystack, ...needleList) {
	return needleList.every(
		/**
		 * @param {any} needle
		 * @returns {boolean}
		 */
		(needle) => haystack.includes(needle)
	);
}

/**
 * @param {any[]} haystack
 * @param {...any} needleList
 * @returns {boolean}
 */
function includesAny(haystack, ...needleList) {
	return needleList.some((needle) => haystack.includes(needle));
}

/**
 * @template {any} ArrayATypes
 * @template {any} ArrayBTypes
 * @param {ArrayATypes[]} a
 * @param {ArrayBTypes[]} b
 * @returns {ArrayATypes[]}
 */
function intersect(a, b) {
	return a.filter(
		/**
		 * @param {any} value
		 * @returns {boolean}
		 */
		(value) => b.includes(value)
	);
}

/**
 * @template {any} Value
 * @param {Value[]} array
 * @returns {Value}
 */
function random(array) {
	return array[Math.floor(Math.random() * array.length)];
}

/**
 * @template {any} Value
 * @param {Value[]} array
 * @returns {Value[]}
 */
function unique(array) {
	return [...new Set(array)];
}

/**
 * @template {any} Value
 * @param {Value} value
 * @returns {Value extends Array<any> ? Value : Value[]}
 */
function wrap(value) {
	// @ts-ignore
	return Array.isArray(value) ? value : [value];
}

/**
 * @template {any[]} Value
 * @param {Value} value
 * @returns {Value[number] | null}
 */
function first(value) {
	return value.find(Boolean) ?? null;
}

/**
 * @template {any[]} Value
 * @param {Value} value
 * @returns {Value[number] | null}
 */
function last(value) {
	return value[value.length - 1] ?? null;
}

/**
 * @template Type
 * @param {Type[]} array
 * @param {Type} fill
 * @param {number} length
 * @returns {Type[]}
 */
function pad(array, fill, length) {
	return Array.from({ ...array, length }).fill(fill, array.length, length);
}

module.exports = {
	chunk,
	diff,
	excludesAll,
	excludesAny,
	first,
	includesAll,
	includesAny,
	intersect,
	last,
	pad,
	random,
	unique,
	wrap,
};
